import React, { useMemo, useState } from 'react';
import { bool, func, number, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import useStyles from 'hooks/useStyles';
import { REGULAR_CLASS, COURSE } from 'constants/contentTypes';
import Button from 'components/Button';
import { CheckIcon, CloseIcon } from 'components/Icon';
import ContentInfo from 'components/ContentInfo';

import styles from './ContentItem.scss';

export default function ContentItem({
  isSelected,
  isSelectable,
  onClick,
  onSelect,
  onRemove,
  name,
  frequency,
  contentType,
  totalLessonsDurationTime,
  lessonCount,
  imageUrl,
  showButton,
}) {
  useStyles(styles);
  const { t } = useTranslation(['purchase']);

  const [isRemovable, setIsRemovable] = useState(false);

  const renderButton = useMemo(() => {
    if (!isSelected)
      return (
        <Button
          block
          reversed
          disabled={!isSelectable}
          text={t('purchase.content.cta.select')}
          onClick={onSelect}
        />
      );

    return (
      <Button
        onMouseEnter={() => setIsRemovable(true)}
        onMouseLeave={() => setIsRemovable(false)}
        block
        type={isRemovable ? 'dark' : 'primary'}
        reversed
        icon={
          isRemovable ? (
            <CloseIcon width={16} height={16} />
          ) : (
            <CheckIcon width={16} height={16} />
          )
        }
        text={
          isRemovable
            ? t('purchase.content.bundlePage.removeCta')
            : t('purchase.content.bundlePage.selectedCta')
        }
        onClick={onRemove}
      />
    );
  }, [t, isSelected, isRemovable, isSelectable, onSelect, onRemove]);

  return (
    <div
      className={cx(styles.root, { [styles.rootClickable]: !!onClick })}
      onClick={onClick}
    >
      <div className={styles.thumbnail}>
        <img className={styles.image} src={imageUrl} alt={name} />
      </div>
      <div className={styles.information}>
        <div className={styles.content}>
          <h5 className={styles.name}>{name}</h5>
          {contentType === REGULAR_CLASS && (
            <p className={styles.frequency}>{frequency}</p>
          )}
          {contentType === COURSE && (
            <ContentInfo
              contentType={COURSE}
              lessonCount={lessonCount}
              totalLessonsDurationTime={totalLessonsDurationTime}
            />
          )}
        </div>
        {showButton && renderButton}
      </div>
    </div>
  );
}

ContentItem.propTypes = {
  imageUrl: string.isRequired,
  name: string.isRequired,
  contentType: string.isRequired,
  frequency: string,
  totalLessonsDurationTime: number,
  lessonCount: number,
  isSelectable: bool,
  isSelected: bool,
  onClick: func,
  onRemove: func,
  onSelect: func,
  showButton: bool,
};

ContentItem.defaultProps = {
  frequency: undefined,
  totalLessonsDurationTime: undefined,
  lessonCount: undefined,
  isSelectable: false,
  isSelected: false,
  onClick: undefined,
  onRemove: () => {},
  onSelect: () => {},
  showButton: true,
};
